import type { StripePriceDto, StripeSubscriptionDto } from '@/features/billing/types';
import { LIBERTY_WEBI_PROMO_CODE } from '@/lib/affiliateReferral';

export type PaymentSummary = {
  dueTodayCents: number;
  firstPaymentCents: number;
  /** Montant réellement reconduit : une promo « once » ne remise que la première facture. */
  renewalCents: number;
  firstPaymentDate: string | null;
  showTrialNotice: boolean;
  hasDiscount: boolean;
  hasRecurringDiscount: boolean;
  promoDeductionCents: number;
  promoCodeLabel: string | null;
};

type PaymentSummaryOptions = {
  allowTrialNotice?: boolean;
  workspacePromoCode?: string | null;
};

function toCents(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function getPaymentSummary(
  price: StripePriceDto,
  options: PaymentSummaryOptions = {},
): PaymentSummary {
  const { allowTrialNotice = true, workspacePromoCode = null } = options;

  const discountedAmount = toCents(price.discountedAmount, price.amount);
  const hasDiscount = price.amount !== discountedAmount;
  const dueTodayCents = toCents(price.amountDueToday, discountedAmount);
  const firstPaymentCents = toCents(price.firstPaymentAmount, discountedAmount);
  const renewalCents = toCents(price.renewalAmount, discountedAmount);

  const showTrialNotice =
    allowTrialNotice &&
    price.trialApplied === true &&
    price.libertyWebiOfferApplied === true &&
    dueTodayCents === 0;

  // La remise peut venir du referral seul : on nomme le code réellement appliqué par le back, pas celui saisi.
  const promoCodeLabel =
    price.libertyWebiOfferApplied === true
      ? LIBERTY_WEBI_PROMO_CODE
      : (workspacePromoCode?.trim() ?? '') || null;

  return {
    dueTodayCents,
    firstPaymentCents,
    renewalCents,
    firstPaymentDate: showTrialNotice ? (price.firstPaymentDate ?? null) : null,
    showTrialNotice,
    hasDiscount,
    hasRecurringDiscount: renewalCents < price.amount,
    promoDeductionCents: Math.max(0, price.amount - discountedAmount),
    promoCodeLabel: hasDiscount ? promoCodeLabel : null,
  };
}

export type RenewalSummary = {
  nextAmountCents: number;
  fullAmountCents: number;
  discountCents: number;
  hasDiscount: boolean;
};

function isDisplayableAmount(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// Le catalogue prévisualise une NOUVELLE souscription : il ignore qu'une promo a pu être consommée.
// On lit donc le montant de la facture à venir dès que le back l'expose, le catalogue n'est qu'un repli.
// Un plan négocié hors catalogue (VIP) n'a ni prix catalogue ni montant : on renvoie null plutôt qu'un
// montant invalide, à charge de l'appelant de ne pas rendre la ligne de tarif.
export function getRenewalSummary(
  subscription: StripeSubscriptionDto,
  matchingPrice: StripePriceDto | null,
): RenewalSummary | null {
  const fullAmountCents = isDisplayableAmount(subscription.amount) ? subscription.amount : null;

  if (isDisplayableAmount(subscription.effectiveAmount)) {
    const nextAmountCents = subscription.effectiveAmount;
    const discountCents = isDisplayableAmount(subscription.discountAmount)
      ? subscription.discountAmount
      : fullAmountCents != null
        ? Math.max(0, fullAmountCents - nextAmountCents)
        : 0;

    return {
      nextAmountCents,
      fullAmountCents: fullAmountCents ?? nextAmountCents,
      discountCents,
      hasDiscount: discountCents > 0,
    };
  }

  if (matchingPrice && isDisplayableAmount(matchingPrice.amount)) {
    const discountCents = isDisplayableAmount(matchingPrice.discountedAmount)
      ? Math.max(0, matchingPrice.amount - matchingPrice.discountedAmount)
      : 0;
    return {
      nextAmountCents: discountCents > 0 ? matchingPrice.discountedAmount : matchingPrice.amount,
      fullAmountCents: matchingPrice.amount,
      discountCents,
      hasDiscount: discountCents > 0,
    };
  }

  if (fullAmountCents == null) return null;

  return {
    nextAmountCents: fullAmountCents,
    fullAmountCents,
    discountCents: 0,
    hasDiscount: false,
  };
}

// La date arrive en YYYY-MM-DD : on la lit en UTC pour éviter un décalage d'un jour selon le fuseau du navigateur.
export function formatFirstPaymentDate(isoDate: string | null, locale: string): string | null {
  if (!isoDate) return null;

  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}
